import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, Users2, Mail, Phone, Calendar, LogOut, Home, FileText, Sparkles } from "lucide-react";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHF = (v) =>
  new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(v || 0);

export default function Partner() {
  const { user, logout, authHeader } = useAuth();
  const nav = useNavigate();
  const [checks, setChecks] = useState([]);
  const [openCheck, setOpenCheck] = useState(null);
  const [copied, setCopied] = useState(false);

  const personalLink = useMemo(
    () => `${window.location.origin}/finanzcheck/${user?.partner_code || user?.id || ""}`,
    [user?.partner_code, user?.id]
  );

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/finanzcheck/mine`, { headers: authHeader() });
        setChecks(r.data.items || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [authHeader]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(personalLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const shareMail = useMemo(() => {
    const firstName = (user?.name || "").trim().split(/\s+/)[0] || "";
    const subject = "Kostenloser Vorsorge- & Finanzcheck – nur für dich";
    const body =
`Hallo,

ich habe kürzlich mit einigen Menschen aus meinem Umfeld über das Thema Finanzen und Vorsorge gesprochen – und mir ist aufgefallen, wie oft die gleiche Unsicherheit auftaucht: „Reicht das, was ich tue, eigentlich aus?"

Genau deshalb möchte ich dir etwas vorstellen, das dir in wenigen Minuten Klarheit gibt – ganz ohne Verkaufsdruck, ohne Fachchinesisch und komplett kostenlos:

▸ Dein persönlicher Vorsorge- & Finanzcheck
${personalLink}

Was dich erwartet:
• 14 kurze Fragen (ca. 3–5 Minuten)
• Sofort eine persönliche Erstanalyse per E-Mail
• Deine CHF-Potenzialrechnung bis zur Pensionierung (steueroptimiert)
• Deine mögliche monatliche Rente ab 65 (einkommenssteuerfrei)
• Konkrete nächste Schritte – klar, ehrlich, auf Augenhöhe

Warum ich dir das schicke:
Weil ich glaube, dass gute finanzielle Entscheidungen mit Klarheit anfangen – nicht mit Produkten. Der Check macht sichtbar, wo du heute stehst und was möglich wäre. Was du damit anfängst, entscheidest du.

Wenn du magst, klick einfach oben auf den Link und probiere es aus. Ich melde mich nach deinen Angaben persönlich bei dir – kurz, unverbindlich und in deinem Tempo.

Bei Fragen: schreib mir einfach zurück.

Herzliche Grüsse${firstName ? "\n" + firstName : ""}
Moonshine Solutions GmbH`;
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [personalLink, user?.name]);

  const shareWhatsApp = useMemo(() => {
    const firstName = (user?.name || "").trim().split(/\s+/)[0] || "";
    const text =
`Hey! 👋

Ich möchte dir kurz etwas zeigen, das dir in wenigen Minuten Klarheit über deine finanzielle Situation gibt – kostenlos, ohne Verkaufsdruck.

*Dein persönlicher Vorsorge- & Finanzcheck:*
${personalLink}

Was dich erwartet:
• 14 kurze Fragen (ca. 3–5 Min.)
• Persönliche Erstanalyse direkt per E-Mail
• Deine CHF-Potenzialrechnung bis 65 (steueroptimiert)
• Mögliche Rente ab 65 (einkommenssteuerfrei)

Klick einfach auf den Link – ich melde mich nach deinen Angaben persönlich bei dir.${firstName ? "\n\nLG " + firstName : ""}`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  }, [personalLink, user?.name]);

  const totalSum = checks.reduce((a, c) => a + (c.projection_65_chf || 0), 0);

  return (
    <div className="min-h-screen bg-ivory-100">
      {/* Header */}
      <header className="border-b border-ink-700/10 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="partner-logo">
            <img src="/moonshine-logo.png" alt="" className="h-9 w-9 rounded-full ring-1 ring-gold-500/30 object-cover" />
            <div className="leading-tight">
              <div className="font-display font-medium text-ink-900 text-sm">Moonshine</div>
              <div className="font-sans text-[10px] uppercase tracking-[0.18em] text-ink-500">Partner-Bereich</div>
            </div>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/Gold" className="text-ink-500 hover:text-ink-900 hidden sm:flex items-center gap-1.5">
              <Sparkles className="w-4 h-4" /> Verträge
            </Link>
            {user?.role === "admin" && (
              <Link to="/backoffice" className="text-ink-500 hover:text-ink-900 hidden sm:flex items-center gap-1.5">
                <Home className="w-4 h-4" /> Backoffice
              </Link>
            )}
            <button onClick={() => { logout(); nav("/login"); }} className="text-ink-500 hover:text-ink-900 flex items-center gap-1.5" data-testid="partner-logout">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10 overflow-x-hidden">
        {/* Greeting */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-gold-700 mb-2">Willkommen zurück</div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight leading-tight text-ink-900 break-words">
            {user?.name || "Partner"}
          </h1>
        </div>

        {/* Grid: Link card + Stats */}
        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          {/* Personal Link Card (2/3) */}
          <div className="lg:col-span-2 rounded-3xl bg-ink-900 text-ivory-100 p-5 sm:p-8 relative overflow-hidden min-w-0">
            <div className="absolute -top-24 -right-20 w-64 h-64 rounded-full bg-gold-500/10 blur-3xl pointer-events-none" />
            <div className="relative flex flex-col sm:flex-row gap-6 items-start min-w-0">
              <div className="flex-1 min-w-0 w-full">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-gold-300 mb-2">Dein persönlicher Finanzcheck-Link</div>
                <p className="text-ivory-100/70 text-sm mb-4">
                  Teile diesen Link mit Interessenten. Jede Einreichung wird automatisch dir zugewiesen und du erhältst die Antworten per E-Mail.
                </p>
                <div className="rounded-2xl bg-ivory-100/5 border border-gold-500/20 mb-3 overflow-hidden">
                  <div className="px-3 sm:px-4 py-2.5 min-w-0">
                    <div className="break-all text-[11px] sm:text-sm text-gold-300 font-mono leading-relaxed" data-testid="partner-link-value">
                      {personalLink}
                    </div>
                  </div>
                  <button
                    onClick={copyLink}
                    data-testid="partner-link-copy"
                    className="w-full border-t border-gold-500/20 py-2.5 text-xs font-semibold text-ink-900 bg-gold-500 hover:bg-gold-400 flex items-center justify-center gap-1.5 transition"
                  >
                    {copied ? (<><Check className="w-3.5 h-3.5" /> Kopiert</>) : (<><Copy className="w-3.5 h-3.5" /> Link kopieren</>)}
                  </button>
                </div>
                <div className="flex gap-4 flex-wrap items-center">
                  <a href={personalLink} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-gold-300 hover:text-gold-100 underline" data-testid="partner-link-open">
                    Vorschau öffnen ↗
                  </a>
                  <a href={shareMail}
                    data-testid="partner-link-mail"
                    className="text-xs text-gold-300 hover:text-gold-100 underline">
                    Per E-Mail teilen
                  </a>
                  <a href={shareWhatsApp}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="partner-link-whatsapp"
                    className="inline-flex items-center gap-1.5 text-xs bg-[#25D366] hover:bg-[#1EBE5D] text-white px-3 py-1.5 rounded-full font-semibold transition">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.99-1.309A9.955 9.955 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/>
                    </svg>
                    WhatsApp
                  </a>
                </div>
              </div>
              {/* QR */}
              <div className="mx-auto sm:mx-0 rounded-2xl bg-white p-3 shadow-lg shrink-0" data-testid="partner-qr">
                <QRCodeSVG value={personalLink} size={112} bgColor="#FFFFFF" fgColor="#0F0F0F" level="M" />
              </div>
            </div>
          </div>

          {/* Stats card (1/3) */}
          <div className="rounded-3xl bg-white border border-ink-700/10 p-5 sm:p-8 flex flex-col justify-between min-w-0">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-500 mb-2">Deine Anfragen</div>
              <div className="font-display text-5xl text-ink-900 font-medium tabular-nums leading-none">
                {checks.length}
              </div>
              <div className="text-xs text-ink-500 mt-1">Finanzchecks insgesamt</div>
            </div>
            <div className="mt-6 pt-6 border-t border-ink-700/10">
              <div className="text-[10px] uppercase tracking-widest text-gold-700 font-bold mb-1">Volumen · Potenzial 65</div>
              <div className="font-display text-xl text-gold-gradient font-medium tabular-nums break-words">
                {CHF(totalSum)}
              </div>
            </div>
          </div>
        </div>

        {/* My finanzchecks */}
        <div className="rounded-3xl bg-white border border-ink-700/10 overflow-hidden">
          <div className="px-6 py-5 border-b border-ink-700/10 flex items-center justify-between">
            <div>
              <h2 className="font-display text-xl font-medium text-ink-900">Deine Finanzcheck-Anfragen</h2>
              <p className="text-sm text-ink-500">Alle Einreichungen, die dir zugeordnet sind.</p>
            </div>
            <Users2 className="w-5 h-5 text-ink-500" />
          </div>

          {checks.length === 0 ? (
            <div className="p-10 text-center text-ink-500 text-sm">
              Noch keine Anfragen. Sobald jemand deinen persönlichen Link nutzt, erscheinen die Einträge hier.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-ivory-100 text-xs uppercase tracking-wider text-ink-500">
                  <tr>
                    <th className="text-left px-4 py-3">Datum</th>
                    <th className="text-left px-4 py-3">Kunde</th>
                    <th className="text-left px-4 py-3 hidden md:table-cell">E-Mail</th>
                    <th className="text-left px-4 py-3 hidden lg:table-cell">Telefon</th>
                    <th className="text-right px-4 py-3 hidden sm:table-cell">Potenzial 65</th>
                    <th className="text-right px-4 py-3">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((c) => (
                    <tr key={c.id} className="border-t border-ink-700/5 hover:bg-ivory-100/60" data-testid={`partner-fc-${c.id}`}>
                      <td className="px-4 py-3 text-xs text-ink-500 tabular-nums">{c.created_at?.slice(0, 10)}</td>
                      <td className="px-4 py-3 font-medium">{c.customer_name}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs text-ink-500">{c.customer_email}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-xs text-ink-500">{c.customer_phone}</td>
                      <td className="px-4 py-3 hidden sm:table-cell text-right text-sm tabular-nums text-gold-700 font-medium">{CHF(c.projection_65_chf)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setOpenCheck(c)} data-testid={`partner-fc-detail-${c.id}`}
                          className="text-xs bg-ink-900 hover:bg-ink-700 text-gold-300 px-3 py-1.5 rounded-full transition">
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Detail modal */}
      {openCheck && (
        <div className="fixed inset-0 bg-ink-900/70 flex items-center justify-center z-50 p-4" onClick={() => setOpenCheck(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-gold-700 mb-1">Finanzcheck</div>
                <h3 className="text-2xl font-display font-medium">{openCheck.customer_name}</h3>
                <div className="text-sm text-ink-500 flex items-center gap-3 flex-wrap mt-1">
                  <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {openCheck.customer_email}</span>
                  <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {openCheck.customer_phone}</span>
                </div>
              </div>
              <button onClick={() => setOpenCheck(null)} className="text-ink-500 hover:text-ink-900 text-xl" data-testid="partner-fc-close">✕</button>
            </div>

            <div className="rounded-xl bg-ink-900 text-ivory-100 px-4 py-4 mb-6">
              <div className="text-[10px] uppercase tracking-widest text-gold-300 mb-1">Potenzial bis 65 · Monatsrente ab 65</div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <div className="font-display text-3xl text-gold-gradient font-medium tabular-nums">{CHF(openCheck.projection_65_chf)}</div>
                <div className="text-sm text-ivory-100/70">→ {CHF(openCheck.rente_max_chf)} / Monat einkommenssteuerfrei</div>
              </div>
            </div>

            <div className="text-sm font-semibold mb-2">Antworten</div>
            <div className="rounded-xl bg-ivory-100 divide-y divide-ink-700/5 text-sm mb-6">
              {Object.entries(openCheck.answers || {}).filter(([k]) => !k.startsWith("gp_")).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-2 px-4 py-2">
                  <div className="text-ink-500 text-xs uppercase tracking-wide">{k}</div>
                  <div className="col-span-2 text-ink-900 break-words">
                    {Array.isArray(v) ? (v.length ? v.join(", ") : <em className="text-ink-500">–</em>) : v === "" || v === null ? <em className="text-ink-500">–</em> : String(v)}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-sm font-semibold mb-2">Versendete Erstanalyse</div>
            <pre className="rounded-xl bg-ink-900 text-ivory-100 text-xs sm:text-sm p-4 whitespace-pre-wrap leading-relaxed max-h-[400px] overflow-auto">
{openCheck.analysis_text || "(Keine Analyse verfügbar)"}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
